<template>
    <div class="order-page">
        <!-- Header Section -->
        <div class="header-section text-center white--text">
            <h1 class="display-1 font-weight-bold mb-2">คำสั่งซื้อ</h1>
        </div>

        <v-container class="order-container mt-6">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-10">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <div v-else>
                <!-- Shipping Address Section -->
                <v-card class="mb-4 pa-4" outlined>
                    <div class="d-flex align-items-center justify-space-between mb-3">
                        <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                            <span class="font-weight-bold subtitle-1">ที่อยู่จัดส่ง</span>
                        </div>
                        <v-btn text color="primary" small @click="editAddress">
                            เปลี่ยน
                        </v-btn>
                    </div>
                    <v-divider class="mb-3"></v-divider>
                    <div class="grey--text text--darken-2">
                        <div class="font-weight-bold mb-1">{{ shippingAddress.name }}</div>
                        <div class="caption">{{ shippingAddress.phone }}</div>
                        <div class="caption">{{ shippingAddress.fullAddress }}</div>
                    </div>
                </v-card>

                <!-- Order Items Section -->
                <v-card class="mb-4 pa-4" outlined>
                    <div class="d-flex align-center mb-3">
                        <v-icon color="primary" class="mr-2">mdi-package-variant</v-icon>
                        <span class="font-weight-bold subtitle-1">รายการสินค้า</span>
                    </div>
                    <v-divider class="mb-4"></v-divider>

                    <!-- Table Header (Once) -->
                    <v-row align="center" class="text-center font-weight-bold grey--text text--darken-1 mb-3">
                        <v-col cols="5" class="text-left">สินค้า</v-col>
                        <v-col cols="3">ราคา</v-col>
                        <v-col cols="2">จำนวน</v-col>
                        <v-col cols="2">รวม</v-col>
                    </v-row>

                    <div v-for="(group, index) in groupedItems" :key="index">
                        <OrderShopGroup :group="group" :show-card="false" :show-header="false" />
                        <v-divider v-if="index < groupedItems.length - 1" class="my-4"></v-divider>
                    </div>
                </v-card>

                <!-- Shipping Method Section -->
                <v-card class="mb-4 pa-4" outlined>
                    <div class="d-flex align-center justify-space-between mb-3">
                        <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-2">mdi-truck-delivery</v-icon>
                            <span class="font-weight-bold subtitle-1">วิธีการจัดส่ง</span>
                        </div>
                    </div>
                    <v-divider class="mb-3"></v-divider>

                    <div v-if="loadingShipping" class="text-center py-4">
                        <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
                    </div>
                    <div v-else>
                        <v-radio-group v-model="selectedShipMasterId" hide-details class="mt-0 pt-0">
                            <div v-for="ship in ships" :key="ship.id" class="d-flex flex-column mb-3">
                                <v-radio :label="ship.shipName" :value="ship.id" color="primary" class="mb-0"></v-radio>
                                <div class="caption grey--text ml-8 mb-1" v-if="ship.condition1">{{ ship.condition1 }}</div>

                                <!-- Sub-level selection -->
                                <v-expand-transition>
                                    <div v-if="selectedShipMasterId === ship.id && hasSubOptions(ship)" class="ml-8 mt-1">
                                        <v-select v-model="selectedVehicleTypeId" :items="getFilteredCompanies(ship.id)"
                                            item-text="name" item-value="id" label="เลือกตัวเลือกเพิ่มเติม" outlined dense
                                            hide-details class="mt-2" background-color="white"></v-select>
                                    </div>
                                </v-expand-transition>
                            </div>
                        </v-radio-group>
                    </div>

                    <div class="mt-4">
                        <v-text-field outlined dense label="หมายเหตุถึงผู้ขาย (ถ้ามี)" v-model="noteToSeller"
                            hide-details></v-text-field>
                    </div>
                </v-card>

                <!-- Order Summary Section -->
                <v-card class="mb-4 pa-4" outlined>
                    <v-row class="mb-2">
                        <v-col cols="6" class="text-left grey--text">ยอดรวมสินค้า</v-col>
                        <v-col cols="6" class="text-right">฿ {{ formatPrice(subtotal) }}</v-col>
                    </v-row>
                    <v-row class="mb-2">
                        <v-col cols="6" class="text-left grey--text">
                            ค่าจัดส่ง 
                            <span v-if="distanceKm > 0" class="caption ml-1 font-weight-normal text--disabled">({{ distanceKm.toFixed(1) }} กม.)</span>
                        </v-col>
                        <v-col cols="6" class="text-right">฿ {{ formatPrice(shippingFee) }}</v-col>
                    </v-row>
                    <v-divider class="my-3"></v-divider>
                    <v-row>
                        <v-col cols="6" class="text-left font-weight-bold">รวมทั้งหมด</v-col>
                        <v-col cols="6" class="text-right font-weight-bold primary--text text-h6">
                            ฿ {{ formatPrice(total) }}
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Shipping error notice -->
                <v-alert
                    v-if="shippingError"
                    type="error"
                    outlined
                    dense
                    class="mb-3"
                >
                    {{ shippingError }}
                </v-alert>

                <!-- Confirm Button -->
                <v-btn
                    color="success"
                    :dark="!shippingError"
                    block large depressed
                    class="mb-6"
                    :disabled="!!shippingError"
                    @click="confirmOrder"
                >
                    ยืนยันคำสั่งซื้อ
                </v-btn>
            </div>
        </v-container>

        <!-- Address Selection Dialog -->
        <v-dialog v-model="showAddressDialog" max-width="600px">
            <v-card class="rounded-lg">
                <v-card-title class="headline font-weight-bold d-flex justify-space-between align-center">
                    เลือกที่อยู่จัดส่ง
                    <v-btn icon @click="showAddressDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4" style="max-height: 400px; overflow-y: auto;">
                    <div v-if="allAddresses.length === 0" class="text-center py-4 grey--text">
                        ไม่พบที่อยู่จัดส่ง
                    </div>
                    <v-card v-for="addr in allAddresses" :key="addr.id" class="mb-3 pa-3 rounded-lg cursor-pointer"
                        :class="addr.id === addressId ? 'selected-address' : 'unselected-address'"
                        @click="selectAddress(addr)" outlined hover>
                        <div class="d-flex align-start">
                            <v-radio-group v-model="addressId" class="mt-0 pt-0 mr-2" hide-details>
                                <v-radio :value="addr.id" color="success"></v-radio>
                            </v-radio-group>
                            <div>
                                <div class="font-weight-bold mb-1">
                                    {{ addr.prefix || '' }} {{ addr.firstName || '' }} {{ addr.lastName || '' }}
                                    <v-chip v-if="addr.id === defaultAddressId" x-small color="error" text-color="white"
                                        class="ml-2">
                                        ค่าเริ่มต้น
                                    </v-chip>
                                </div>
                                <div class="text-caption grey--text text--darken-2">{{ addr.phone }}</div>
                                <div class="text-caption grey--text text--darken-2 mt-1">{{ formatFullAddress(addr) }}
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text color="grey darken-1" @click="showAddressDialog = false">ยกเลิก</v-btn>
                    <v-btn color="success" @click="$router.push('/profile_user?tab=profile_address')">
                        จัดการที่อยู่
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import OrderShopGroup from "@/components/Order/OrderShopGroup.vue";
import Swal from "sweetalert2";
import { getCartApi } from "@/services/api/cart";
import { createOrderFromCart, calculateShippingFeeApi } from "@/services/api/order";
import { getMyAddresses } from "@/services/api/userServices";
import PaymentMethodService from "@/services/api/paymentMethod";
import { getShipsApi, getShippingCompaniesApi } from "@/services/api/adminServices";
import WalletService from "@/services/api/wallet";

export default {
    name: "Order",
    components: {
        OrderShopGroup,
    },
    data() {
        return {
            loading: false,
            orderItems: [],
            shippingAddress: {
                name: "",
                phone: "",
                fullAddress: "",
            },
            addressId: null,
            defaultAddressId: null,
            allAddresses: [],
            showAddressDialog: false,
            shippingMethod: {
                name: "ขนส่งเอกชน",
                description: "จัดส่งภายใน 1-2 วันทำการ (ขึ้นอยูกับพื้นที่)",
            },
            paymentMethod: {
                name: "ชำระเงิน",
            },
            selectedPayment: "QRCODE",
            availablePaymentMethods: [],
            walletBalance: 0,
            noteToSeller: "",
            shippingFee: 0,
            distanceKm: 0,
            shippingError: "",
            processing: false,
            selectedItemIds: [],

            // Shipping Options
            loadingShipping: false,
            ships: [],
            vehicleTypes: [],
            selectedShipMasterId: null,
            selectedVehicleTypeId: null,
            selectedServiceTypeId: null,
        };
    },
    computed: {
        groupedItems() {
            if (!this.orderItems || this.orderItems.length === 0) return [];

            const groups = {};

            this.orderItems.forEach((item) => {
                const seller = item.product?.seller;
                const shopName = seller?.shopName || "ร้านค้าทั่วไป";
                const sellerId = seller?.id || item.sellerId || "unknown";

                if (!groups[sellerId]) {
                    groups[sellerId] = {
                        sellerId,
                        shopName,
                        seller,
                        items: [],
                    };
                }
                groups[sellerId].items.push(item);
            });

            return Object.values(groups);
        },
        subtotal() {
            return this.orderItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
        },
        total() {
            return this.subtotal + this.shippingFee;
        },
    },
    watch: {
        selectedShipMasterId(newVal) {
            this.selectedVehicleTypeId = null;
            if (newVal && this.addressId) this.recalcShippingFee();
        },
        selectedVehicleTypeId() {
            if (this.selectedShipMasterId && this.addressId) this.recalcShippingFee();
        },
        addressId(newVal) {
            if (newVal && this.selectedShipMasterId) this.recalcShippingFee();
        }
    },
    async mounted() {
        await this.loadOrderData();
    },
    methods: {
        async recalcShippingFee() {
            if (this.orderItems.length === 0) return;
            this.shippingError = "";
            if (!this.selectedShipMasterId || !this.addressId) {
                 this.shippingFee = 0;
                 return;
            }

            // ถ้าเป็นขนส่งแบบที่ต้องเลือกประเภทย่อย (Rider) ต้องรอให้เลือกก่อน
            const shipObj = this.ships.find(s => s.id === this.selectedShipMasterId);
            if (shipObj && this.hasSubOptions(shipObj) && !this.selectedVehicleTypeId) {
                this.shippingFee = 0;
                return;
            }

            try {
                const sellerId = this.orderItems[0]?.product?.seller?.id || this.orderItems[0]?.sellerId;
                if (!sellerId || sellerId === "unknown") return;

                const payload = {
                    sellerId,
                    shipId: this.selectedShipMasterId,
                    vehicleTypeId: this.selectedVehicleTypeId,
                    itemsTotal: this.subtotal,
                    addressId: this.addressId
                };
                const res = await calculateShippingFeeApi(payload);
                if (res.success) {
                    this.shippingFee = res.fee || 0;
                    this.distanceKm = res.distanceKm || 0;
                    this.selectedServiceTypeId = res.serviceTypeId || null;
                    this.shippingError = "";
                }
            } catch (error) {
                console.error("Error recalculating shipping fee:", error);
                const msg = error.response?.data?.message || "ระบบไม่สามารถคำนวณค่าจัดส่งได้";
                if (msg) {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 5000,
                        title: "ไม่สามารถคำนวณค่าจัดส่งได้",
                        text: msg,
                        icon: "warning"
                    });
                }
                // Reset to 0 when calculation fails
                this.shippingFee = 0;
                this.distanceKm = 0;
                this.shippingError = msg || "ไม่สามารถคำนวณค่าจัดส่งได้";
            }
        },
        async loadOrderData() {
            this.loading = true;

            try {

                // First check if we have data passed from Cart page
                const savedOrderData = localStorage.getItem('orderData');
                let selectedItemIds = [];

                if (savedOrderData) {
                    try {
                        const parsed = JSON.parse(savedOrderData);
                        if (parsed.items && Array.isArray(parsed.items)) {
                            this.orderItems = parsed.items;
                            selectedItemIds = parsed.items.map(i => i.id);
                            this.selectedItemIds = selectedItemIds;

                            // Check single seller
                            const sellerIds = [...new Set(this.orderItems.map(i => i.product?.seller?.id || i.sellerId || "unknown"))];
                            if (sellerIds.length > 1) {
                                await Swal.fire({
                                    title: "จัดส่งสินค้าทีละร้านค้า",
                                    text: "กรุณาเลือกสินค้าเพียงร้านค้าเดียวเพื่อทำการสั่งซื้อ",
                                    icon: "warning",
                                });
                                this.$router.push({ name: "cart" });
                                return;
                            }
                        }
                    } catch (e) {
                        console.error("Error parsing orderData from localStorage", e);
                    }
                }

                // Fallback to fetching active cart if nothing in localStorage
                if (this.orderItems.length === 0) {
                    const cartResponse = await getCartApi();

                    if (cartResponse.success && cartResponse.data) {
                        const cart = cartResponse.data;
                        this.orderItems = cart.items || [];
                        this.shippingFee = cart.shippingFee || 0;

                        if (this.orderItems.length === 0) {
                            await Swal.fire({
                                title: "ตะกร้าว่างเปล่า",
                                text: "กรุณาเพิ่มสินค้าในตะกร้าก่อนทำการสั่งซื้อ",
                                icon: "warning",
                            });
                            this.$router.push({ name: "cart" });
                            return;
                        }

                        // Ensure only one seller
                        const sellerIds = [...new Set(this.orderItems.map(i => i.product?.seller?.id || i.sellerId || "unknown"))];
                        if (sellerIds.length > 1) {
                            await Swal.fire({
                                title: "จัดส่งสินค้าทีละร้านค้า",
                                text: "กรุณาเลือกสินค้าเพียงร้านค้าเดียวเพื่อทำการสั่งซื้อ",
                                icon: "warning",
                            });
                            this.$router.push({ name: "cart" });
                            return;
                        }
                    }
                }

                // Load user's addresses
                const addressResponse = await getMyAddresses();
                const addresses = addressResponse.items || [];
                const defaultAddressId = addressResponse.defaultAddress;

                this.allAddresses = addresses;
                this.defaultAddressId = defaultAddressId;

                // Load Shipping Options 
                await this.fetchShippingOptions();

                if (addresses.length > 0) {
                    // Find default address or use first
                    const selectedAddress = defaultAddressId
                        ? addresses.find(addr => addr.id === defaultAddressId)
                        : null;

                    const address = selectedAddress || addresses[0];

                    this.addressId = address.id;
                    this.shippingAddress = {
                        name: `${address.prefix || ''} ${address.firstName || ''} ${address.lastName || ''}`.trim() || "",
                        phone: address.phone || "",
                        fullAddress: this.formatFullAddress(address),
                    };
                } else {
                    await Swal.fire({
                        title: "ไม่พบที่อยู่จัดส่ง",
                        text: "กรุณาเพิ่มที่อยู่จัดส่งก่อนทำการสั่งซื้อ",
                        icon: "warning",
                    });
                    this.$router.push({ name: "profile_user", query: { tab: "profile_address" } });
                    return;
                }

                // Load Payment Methods
                const pmRes = await PaymentMethodService.getPaymentMethods();
                if (pmRes.data && pmRes.data.data) {
                    this.availablePaymentMethods = pmRes.data.data;
                    if (this.availablePaymentMethods.length > 0) {
                        this.selectedPayment = this.availablePaymentMethods[0].code;
                    }
                }

                // Load Wallet Balance for ELSON_CASH
                const walletRes = await WalletService.getMyWallet();
                if (walletRes.data && walletRes.data.topUp) {
                    this.walletBalance = walletRes.data.topUp.balance;
                }

                // Calc initial handled by watchers now automatically since addressId gets set here
            } catch (error) {
                console.error("Error loading order data:", error);
                await Swal.fire({
                    title: "เกิดข้อผิดพลาด",
                    text: error.response?.data?.message || "ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้",
                    icon: "error",
                });
                this.$router.push({ name: "home" });
            } finally {
                this.loading = false;
            }
        },
        formatFullAddress(address) {
            const parts = [
                address.detail,
                address.subdistrict,
                address.district,
                address.province,
                address.postalCode
            ].filter(Boolean);
            return parts.join(" ");
        },
        formatPrice(val) {
            return Number(val).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        editAddress() {
            this.showAddressDialog = true;
        },
        selectAddress(addr) {
            this.addressId = addr.id;
            this.shippingAddress = {
                name: `${addr.prefix || ''} ${addr.firstName || ''} ${addr.lastName || ''}`.trim() || "",
                phone: addr.phone || "",
                fullAddress: this.formatFullAddress(addr),
            };
            this.showAddressDialog = false;
        },
        async confirmOrder() {
            if (this.processing) return;

            // Block ถ้าค่าจัดส่งคำนวณไม่ได้ (เช่น ระยะทางเกินกำหนด)
            if (this.shippingError) {
                await Swal.fire({
                    title: "ไม่สามารถสั่งซื้อได้",
                    text: this.shippingError,
                    icon: "error",
                });
                return;
            }

            // Validate Rider Selection
            const shipObj = this.ships.find(s => s.id === this.selectedShipMasterId);
            if (shipObj && this.hasSubOptions(shipObj) && !this.selectedVehicleTypeId) {
                await Swal.fire({
                    title: "กรุณาเลือกประเภทรถจัดส่ง",
                    text: "คุณเลือกการจัดส่งด่วนผ่าน Elson Rider โปรดเลือกประเภทรถที่ต้องการ",
                    icon: "warning",
                });
                return;
            }

            const result = await Swal.fire({
                title: "ยืนยันคำสั่งซื้อ",
                text: `ยอดรวมทั้งหมด ${this.formatPrice(this.total)} บาท`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#198C19",
                cancelButtonColor: "#d33",
                confirmButtonText: "ยืนยัน",
                cancelButtonText: "ยกเลิก",
            });

            if (!result.isConfirmed) return;

            this.processing = true;

            try {
                // Step 1: Create order(s) from cart (status: pending)
                const orderPayload = {
                    addressRef: this.addressId,
                    paymentMethod: this.selectedPayment,
                    shipId: this.selectedShipMasterId,
                    vehicleTypeId: this.selectedVehicleTypeId,
                    shipmentFee: this.shippingFee,
                    serviceTypeId: this.selectedServiceTypeId,
                    discountTotal: 0,
                    itemIds: this.selectedItemIds,
                };

                const orderResponse = await createOrderFromCart(orderPayload);

                const createdOrders = orderResponse.data;
                const allPaid = orderResponse.allPaid;

                // Step 2: Handle different payment methods
                if (allPaid) {
                    // If fully paid (e.g. via ELSON_CASH), go to success page
                    this.$router.push({
                        name: "payment-success",
                        params: {
                            orderId: createdOrders[0].id,
                            orderIds: createdOrders.map(o => o.id),
                            orderNumbers: createdOrders.map(o => o.orderNumber)
                        },
                    });
                } else if (this.selectedPayment === 'QRCODE') {
                    // Navigate to QR payment page
                    this.$router.push({
                        name: "qr-payment",
                        params: {
                            orderId: createdOrders[0].id,
                            orderIds: createdOrders.map(o => o.id),
                            total: this.total,
                        },
                    });
                } else {
                    // Default to order list / detail if pending (e.g. if Wallet payment failed or other method)
                    if (orderResponse.walletError) {
                        await Swal.fire({
                            title: "ชำระเงินไม่สำเร็จ",
                            text: orderResponse.message,
                            icon: "warning"
                        });
                    }
                    this.$router.push({
                        name: "profile_user",
                        query: { tab: "orders_tracking" }
                    });
                }

                // Clear orderData from localStorage after success
                localStorage.removeItem('orderData');

            } catch (error) {
                console.error("Error confirming order:", error);

                const errorMessage = error.response?.data?.message || error.message || "เกิดข้อผิดพลาดในการสั่งซื้อ";

                await Swal.fire({
                    title: "เกิดข้อผิดพลาด",
                    text: errorMessage,
                    icon: "error",
                });
            } finally {
                this.processing = false;
            }
        },

        async fetchShippingOptions() {
            this.loadingShipping = true;
            try {
                const [resShips, resCompanies] = await Promise.all([
                    getShipsApi(),
                    getShippingCompaniesApi()
                ]);

                this.ships = resShips.data || [];
                this.vehicleTypes = resCompanies.data || [];

                if (this.ships.length > 0) {
                    // Default select the first one
                    this.selectedShipMasterId = this.ships[0].id;
                }
            } catch (e) {
                console.error("fetchShippingOptions error:", e);
            } finally {
                this.loadingShipping = false;
            }
        },

        hasSubOptions(ship) {
            // "ถ้าเป็น elson rider ให้เลือกระดับย่อย ตาราง vehicle_types ได้ แต่ถ้า ขนส่งเอกชน ไม่ต้องมีให้เลือกต่อแล้ว"
            // Let's implement this logic based on naming since instruction is specific
            const name = (ship.shipName || "").toLowerCase();
            if (name.includes("elson") || name.includes("rider")) return true;
            return false;
        },

        getFilteredCompanies(shipId) {
            return this.vehicleTypes.filter(c => c.shipId === shipId);
        },
    },
};
</script>

<style scoped>
.order-page {
    background-color: #f5f5f5;
    min-height: 100vh;
}

.header-section {
    background: linear-gradient(90deg, #F4811F 0%, #FFF5E6 100%);
    padding: 40px 0;
}

@media (max-width: 600px) {
    .header-section {
        padding: 24px 0;
    }

    .header-section h1 {
        font-size: 1.5rem !important;
    }
}

.order-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 40px;
    box-sizing: border-box;
}

@media (min-width: 1440px) and (max-width: 1919px) {
    .order-container {
        max-width: 1200px;
    }
}

@media (max-width: 768px) {
    .order-container {
        padding: 0 16px;
    }
}

.selected-address {
    border: 2px solid #198C19 !important;
    background-color: #F8FDF9;
}

.unselected-address {
    border: 1px solid #E0E0E0 !important;
}

.cursor-pointer {
    cursor: pointer;
}

.cursor-pointer:hover {
    background-color: #fafafa;
}
</style>
